import React from 'react';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useFinancial} from '../../../context/FinancialContext';
import QuickAddForm from './QuickAddForm';
import {fmt, sumMonthly, isDiscretionary, getEmergencyBase, getRetireSpending} from './walkthroughUtils';

const {FiUnlock, FiTrendingUp, FiHeart, FiBriefcase, FiLayers, FiArrowRight, FiCalendar} = FiIcons;
const ACCOUNTS = [
  {icon: FiHeart, name: 'HSA', benefit: 'Triple tax advantage', detail: 'If eligible, save for qualified medical expenses with unique tax benefits.'},
  {icon: FiTrendingUp, name: 'Roth IRA', benefit: 'Tax-free withdrawals', detail: 'Consider eligibility, income limits and your expected tax picture.'},
  {icon: FiBriefcase, name: '401(k)', benefit: 'Workplace retirement', detail: 'Beyond the employer match, review plan fees and traditional vs Roth choices.'}
];

export const TaxTool = () => (
  <div className="space-y-2">
    {ACCOUNTS.map((account, index) => (
      <div key={account.name} className="flex gap-3 p-3 bg-white border border-slate-100 rounded-xl shadow-sm">
        <span className="text-[10px] font-black text-blue-600">{index + 1}.</span>
        <div><p className="text-[11px] font-black text-slate-900">{account.name} · {account.benefit}</p><p className="text-[10px] text-slate-500">{account.detail}</p></div>
      </div>
    ))}
    <p className="text-[9px] font-medium text-slate-400">Account order varies with eligibility, tax situation and plan rules. Roth withdrawals must meet applicable rules to be tax-free.</p>
  </div>
);

export const TaxVisual = () => (
  <div className="space-y-4">
    <div className="p-5 bg-white rounded-3xl border border-slate-100 shadow-sm">
      <p className="text-[9px] font-black uppercase text-blue-600 tracking-widest mb-4">Different buckets, different benefits</p>
      <div className="grid grid-cols-3 gap-2">
        {ACCOUNTS.map((account, index) => (
          <div key={account.name} className="p-3 rounded-2xl bg-blue-50 border border-blue-100">
            <div className="w-9 h-9 rounded-xl bg-white text-blue-600 flex items-center justify-center mb-3"><SafeIcon icon={account.icon} /></div>
            <p className="text-[10px] font-black text-slate-900">{account.name}</p>
            <p className="text-[9px] font-medium text-slate-500 mt-1 leading-snug">{account.benefit}</p>
            <div className="h-1 bg-blue-100 rounded-full mt-3"><div className="h-1 bg-blue-500 rounded-full" style={{width: `${100 - index * 25}%`}} /></div>
          </div>
        ))}
      </div>
    </div>
    <div className="p-4 bg-blue-600 rounded-2xl text-white">
      <p className="text-[10px] font-black">Make each dollar work in the right place.</p>
      <p className="text-[9px] font-medium text-blue-100 mt-1">Check current limits and your tax position before deciding what to fund next.</p>
    </div>
  </div>
);

export const BridgeTool = () => (
  <div className="space-y-3">
    <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl">
      <h4 className="text-[10px] font-black text-teal-900 mb-2">Roth conversion ladder</h4>
      <p className="text-[10px] text-teal-800 font-semibold">Convert eligible pre-tax funds to a Roth IRA in planned annual amounts. Converted taxable amounts generally need five tax years before penalty-free early withdrawal. Taxes and other rules still apply.</p>
    </div>
    <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
      <h4 className="text-[10px] font-black text-slate-900 mb-1">Fund the waiting period</h4>
      <p className="text-[10px] text-slate-600">Plan accessible cash or taxable investments for the years before conversions become available. A backdoor Roth contribution is a different strategy, not a substitute for this bridge.</p>
    </div>
  </div>
);

export const BridgeVisual = () => (
  <div className="space-y-4">
    <div className="bg-white p-5 rounded-3xl border border-teal-100 shadow-sm">
      <div className="flex items-center gap-2 mb-4"><SafeIcon icon={FiUnlock} className="text-teal-600" /><p className="text-[9px] font-black uppercase text-teal-700 tracking-widest">How the bridge works</p></div>
      <div className="grid grid-cols-3 gap-2">
        {[['01', 'Convert', 'Plan taxes on a conversion'], ['02', 'Bridge', 'Use accessible savings'], ['03', 'Access', 'Check the 5-year rule']].map(([number, title, detail]) => (
          <div key={number} className="p-3 bg-teal-50 rounded-xl border border-teal-100">
            <p className="text-lg font-black text-teal-500">{number}</p><p className="text-[10px] font-black text-slate-900">{title}</p><p className="text-[9px] font-medium text-slate-500 mt-1">{detail}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="p-4 bg-teal-600 rounded-2xl text-white">
      <p className="text-[10px] font-black">Start planning before you stop working.</p>
      <p className="text-[9px] text-teal-100 mt-1">Conversion timing, tax bills and access rules make a year-by-year plan important.</p>
    </div>
  </div>
);

export const LeverTool = () => {
  const {state, dispatch} = useFinancial();
  const gap = Math.max(0, state.calculations.requiredMonthlyInvestment - state.calculations.monthlyInvested);
  const available = Math.max(0, state.calculations.netSurplus);
  const amount = Math.min(gap, available);
  const allocate = () => {
    if (amount <= 0) return;
    dispatch({type: 'ADD_INVESTMENT', payload: {id: Date.now() + Math.random(), name: 'FI Goal Contribution', amount: Math.round(amount * 100) / 100, frequency: 'monthly', accountType: 'investing_transfer', icon: 'TrendingUp', color: 'blue'}});
  };
  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
        <p className="text-[8px] font-black uppercase text-blue-500 mb-1">Monthly contribution gap</p>
        <p className="text-xl font-black text-blue-900">{fmt(gap)}</p>
        <p className="text-[10px] font-medium text-blue-700 mt-1">Increasing income creates room; a Budget investment is what actually directs that room toward FI.</p>
      </div>
      <div className="space-y-2">
        <p className="text-[9px] font-black uppercase text-emerald-600">First: add new income to Budget</p>
        <QuickAddForm kind="income" placeholder="e.g. Raise or side work" />
      </div>
      <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
        <p className="text-[9px] font-black uppercase text-blue-600">Then: direct available cash to FI</p>
        <p className="text-[10px] text-slate-600">Available after your existing expenses and investments: <strong>{fmt(available)}/mo</strong></p>
        <button disabled={amount <= 0} onClick={allocate} className="w-full py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase disabled:bg-slate-200 disabled:text-slate-400 hover:bg-blue-700">Add {fmt(amount)}/mo to investments</button>
        <p className="text-[9px] text-slate-400">Adds a monthly FI Goal Contribution to Budget. It does not transfer money at your bank.</p>
      </div>
    </div>
  );
};

export const LeverVisual = () => {
  const {state} = useFinancial();
  const gap = Math.max(0, state.calculations.requiredMonthlyInvestment - state.calculations.monthlyInvested);
  const available = Math.max(0, state.calculations.netSurplus);
  return (
    <div className="space-y-4">
      <div className="p-5 bg-white rounded-3xl border border-slate-100 shadow-sm">
        <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-4">Where the new income goes</p>
        <div className="space-y-2">
          {[
            ['1', 'Income Hub', fmt(sumMonthly(state.incomeStreams)), 'bg-emerald-50 text-emerald-700'],
            ['2', 'Available after Budget', fmt(available), 'bg-slate-100 text-slate-700'],
            ['3', 'Investing Transfer', fmt(state.calculations.monthlyInvested), 'bg-blue-50 text-blue-700']
          ].map(([number, label, value, style], index) => (
            <React.Fragment key={label}>
              {index > 0 && <SafeIcon icon={FiArrowRight} className="text-slate-300 mx-auto rotate-90" />}
              <div className={`p-3 rounded-xl flex items-center gap-3 ${style}`}>
                <span className="w-6 h-6 bg-white/70 rounded-lg text-[10px] font-black flex items-center justify-center">{number}</span>
                <span className="flex-1 text-[10px] font-black">{label}</span><span className="text-sm font-black">{value}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-[10px] font-semibold text-blue-900">
        {gap > 0 ? `${fmt(gap)}/mo remains to reach the FI contribution goal. Add income, then allocate the available amount on the left.` : 'Your monthly contribution already meets the FI goal. Extra income can accelerate it further.'}
      </div>
    </div>
  );
};

export const ReviewTool = ({shared}) => {
  const {state} = useFinancial();
  const required = sumMonthly(state.expenses.filter(item => !isDiscretionary(item)));
  const emergency = getEmergencyBase(shared, required);
  const retirement = getRetireSpending(shared, state.calculations.monthlyExpenses);
  const gap = Math.max(0, state.calculations.requiredMonthlyInvestment - state.calculations.monthlyInvested);
  const commitments = [
    `Keep ${fmt(emergency * 3)}–${fmt(emergency * 6)} accessible for emergencies.`,
    `Aim for ${fmt(retirement * 12 * shared.multiplier)} invested by age ${state.goals.targetAge}.`,
    `Invest ${fmt(state.calculations.monthlyInvested)}/mo now${gap > 0 ? `; plan how to close the ${fmt(gap)}/mo gap` : '; maintain or improve the pace'}.`,
    'Review spending, income, balances and allocations every 12 months.'
  ];
  return (
    <div className="space-y-3">
      <p className="text-[9px] font-black uppercase tracking-widest text-blue-600">Your plan to carry forward</p>
      {commitments.map((text, index) => (
        <div key={text} className="flex gap-3 p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
          <span className="w-6 h-6 flex-shrink-0 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black flex items-center justify-center">{index + 1}</span>
          <p className="text-[10px] font-semibold text-slate-700">{text}</p>
        </div>
      ))}
      <p className="text-[9px] font-medium text-slate-400">These are planning commitments, not automatic bank transfers or calendar reminders.</p>
    </div>
  );
};

export const ReviewVisual = ({shared}) => {
  const {state} = useFinancial();
  const {calculations, roadmapProgress} = state;
  const nextReview = new Date();
  nextReview.setFullYear(nextReview.getFullYear() + 1);
  const rows = [
    ['FI target', fmt(getRetireSpending(shared, calculations.monthlyExpenses) * 12 * shared.multiplier)],
    ['Monthly contribution', fmt(calculations.monthlyInvested)],
    ['Required monthly', fmt(calculations.requiredMonthlyInvestment)],
    ['Estimated wealth at target age', fmt(calculations.projectedWealth)]
  ];
  return (
    <div className="space-y-4">
      <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><SafeIcon icon={FiLayers} className="text-blue-600" /><p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Your plan, at a glance</p></div>
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 items-center py-3 border-b border-slate-100 last:border-0">
            <span className="text-[10px] font-semibold text-slate-500">{label}</span><span className="text-sm font-black text-slate-900 text-right">{value}</span>
          </div>
        ))}
      </div>
      <div className="p-5 bg-blue-600 text-white rounded-2xl flex gap-3 items-start">
        <SafeIcon icon={FiCalendar} className="text-xl flex-shrink-0" />
        <div>
          <p className="text-[10px] font-black uppercase text-blue-100">Next annual check-in</p>
          <p className="text-lg font-black">{nextReview.toLocaleDateString('en-US', {month: 'long', year: 'numeric'})}</p>
          <p className="text-[9px] text-blue-100 mt-1">Recheck expenses, FI number and investment pace. {roadmapProgress.length}/11 dashboard milestones marked complete.</p>
        </div>
      </div>
    </div>
  );
};